import type { TrainingErrorCode, TrainingResult } from '../domain/training.ts';

export interface TrainingIntent<T> { run(): Promise<TrainingResult<T>> }
// One object per explicit intent. Retries retain the command, IDs and observed context.
export function intent<C, T>(prepare: () => Promise<TrainingResult<C>>,
  write: (command: C) => Promise<TrainingResult<unknown>>,
  read: (command: C) => Promise<TrainingResult<T>>): TrainingIntent<T> {
  let command: C;
  let prepared = false;
  let committed = false;
  let running: Promise<TrainingResult<T>> | undefined;
  async function run(): Promise<TrainingResult<T>> {
    if (!prepared) {
      const result = await prepare(); if (!result.ok) return result;
      command = result.value; prepared = true;
    }
    if (!committed) {
      const result = await write(command); if (!result.ok) return result;
      committed = true;
    }
    return read(command);
  }
  return { run() { running ??= run().finally(() => { running = undefined; }); return running; } };
}
export const trainingMessages: Record<TrainingErrorCode, string> = {
  VALIDATION_ERROR: 'Revisa los campos. No se ha guardado.',
  NOT_FOUND: 'No se encuentra el registro o una revisión referenciada. Vuelve a consultar.',
  REVISION_CONFLICT: 'El registro cambió en otra pestaña. Conservamos tu entrada; vuelve a consultar antes de decidir.',
  INVARIANT_CONFLICT: 'La operación no es compatible con el estado actual. Vuelve a consultar.',
  DATASET_STALE: 'El conjunto de datos cambió. Vuelve a consultar antes de guardar.',
  RESTORE_IN_PROGRESS: 'Los datos están protegidos por una operación de recuperación. Reintenta más tarde.',
  STORAGE_QUOTA: 'No hay espacio para guardar. Conservamos tu entrada.',
  STORAGE_UNAVAILABLE: 'No se pudo confirmar el resultado. Reintenta la misma intención.',
  TRANSACTION_ABORTED: 'No se guardaron los cambios. Puedes reintentar.',
  CORRUPTION_DETECTED: 'Los datos no son coherentes. La operación se ha detenido.',
  SCHEMA_UNSUPPORTED: 'Estos datos requieren otra versión de la aplicación.',
  UPGRADE_BLOCKED: 'Otra pestaña bloquea la apertura. Ciérrala y recarga.',
};
